/**
 * Abstract Storage Service
 * 
 * This service provides a unified interface for data storage operations.
 * It abstracts the underlying storage mechanism (localStorage, sessionStorage, etc.)
 * allowing easy switching between different storage types without code changes.
 * 
 * Features:
 * - Automatic JSON serialization/deserialization
 * - AES encryption for sensitive data storage
 * - Error handling with fallback values
 * - Type checking and validation
 * - Support for different storage backends
 * - Consistent API across the application
 */

import cryptoService from '../security/crypto.service.js';

// Storage backend types
const STORAGE_TYPES = {
    LOCAL: 'localStorage',
    SESSION: 'sessionStorage',
    MEMORY: 'memory'
};

// In-memory storage fallback for when localStorage/sessionStorage is not available
const inMemoryStorage = {};

class StorageService {
    constructor(storageType = STORAGE_TYPES.LOCAL, options = {}) {
        this.storageType = storageType;
        this.storage = this._getStorageBackend(storageType);
        
        // Encryption configuration
        this.encryptionEnabled = options.enableEncryption !== undefined 
            ? options.enableEncryption 
            : this._shouldEnableEncryption(storageType);
    }

    /**
     * Determine if encryption should be enabled for this storage type
     * @param {string} storageType - Storage type
     * @returns {boolean} Whether encryption should be enabled
     */
    _shouldEnableEncryption(storageType) {
        if (storageType === STORAGE_TYPES.MEMORY) {
            return process.env.NODE_ENV === 'production';
        }
        
        // Encryption is enabled when the crypto service has been initialized with a token.
        // Before login (no token yet), storage reads/writes fall back to unencrypted.
        return cryptoService.isEnabled();
    }

    /**
     * Get the appropriate storage backend
     * @param {string} type - Storage type
     * @returns {Storage|Object} Storage backend
     */
    _getStorageBackend(type) {
        try {
            switch (type) {
                case STORAGE_TYPES.LOCAL:
                    // Test if localStorage is available
                    if (typeof window !== 'undefined' && window.localStorage) {
                        const testKey = '__storage_test__';
                        window.localStorage.setItem(testKey, 'test');
                        window.localStorage.removeItem(testKey);
                        return window.localStorage;
                    }
                    break;
                case STORAGE_TYPES.SESSION:
                    // Test if sessionStorage is available
                    if (typeof window !== 'undefined' && window.sessionStorage) {
                        const testKey = '__storage_test__';
                        window.sessionStorage.setItem(testKey, 'test');
                        window.sessionStorage.removeItem(testKey);
                        return window.sessionStorage;
                    }
                    break;
                case STORAGE_TYPES.MEMORY:
                default:
                    return {
                        getItem: (key) => inMemoryStorage[key] || null,
                        setItem: (key, value) => { inMemoryStorage[key] = value; },
                        removeItem: (key) => { delete inMemoryStorage[key]; },
                        clear: () => { Object.keys(inMemoryStorage).forEach(key => delete inMemoryStorage[key]); },
                        key: (index) => Object.keys(inMemoryStorage)[index] || null,
                        get length() { return Object.keys(inMemoryStorage).length; }
                    };
            }
        } catch (error) {
            // Storage type not available, use memory fallback
        }
        
        // Fallback to memory storage
        return this._getStorageBackend(STORAGE_TYPES.MEMORY);
    }

    /**
     * Hash a storage key for security (only when crypto is initialized)
     * @param {string} originalKey - The original storage key
     * @returns {string} Hashed key (or original if crypto not ready)
     * @private
     */
    _hashKey(originalKey) {
        // Re-check encryption status dynamically (crypto may have been initialized since construction)
        if (cryptoService.canHashKeys()) {
            try {
                return cryptoService.hashStorageKey(originalKey);
            } catch (error) {
                // Crypto not ready yet (pre-login), use plain key
                return originalKey;
            }
        }
        return originalKey;
    }

    /**
     * Store data in the storage
     * @param {string} key - Storage key
     * @param {any} value - Value to store (will be JSON stringified)
     * @param {Object} options - Additional options
     * @returns {boolean} Success status
     */
    setItem(key, value, options = {}) {
        try {
            if (!key) {
                throw new Error('Storage key is required');
            }

            const hashedKey = this._hashKey(key);
            const serializedValue = this._serialize(value);
            this.storage.setItem(hashedKey, serializedValue);
            
            // Optional: Add expiration support
            if (options.expiresIn) {
                const expirationTime = Date.now() + options.expiresIn;
                const hashedExpirationKey = this._hashKey(`${key}_expires`);
                this.storage.setItem(hashedExpirationKey, expirationTime.toString());
            }

            return true;
        } catch (error) {
            return false;
        }
    }

    /**
     * Retrieve data from storage
     * @param {string} key - Storage key
     * @param {any} defaultValue - Default value if key doesn't exist
     * @returns {any} Retrieved value or default
     */
    getItem(key, defaultValue = null) {
        try {
            if (!key) {
                return defaultValue;
            }

            // Check expiration if it exists
            const hashedExpirationKey = this._hashKey(`${key}_expires`);
            const expirationTime = this.storage.getItem(hashedExpirationKey);
            if (expirationTime && Date.now() > parseInt(expirationTime)) {
                this.removeItem(key);
                this.storage.removeItem(hashedExpirationKey);
                return defaultValue;
            }

            const hashedKey = this._hashKey(key);
            const item = this.storage.getItem(hashedKey);
            
            if (item === null || item === undefined) {
                return defaultValue;
            }

            return this._deserialize(item);
        } catch (error) {
            return defaultValue;
        }
    }

    /**
     * Remove an item from storage
     * @param {string} key - Storage key
     * @returns {boolean} Success status
     */
    removeItem(key) {
        try {
            if (!key) {
                return false;
            }

            const hashedKey = this._hashKey(key);
            this.storage.removeItem(hashedKey);
            
            // Also remove expiration if it exists
            const hashedExpirationKey = this._hashKey(`${key}_expires`);
            this.storage.removeItem(hashedExpirationKey);
            
            return true;
        } catch (error) {
            return false;
        }
    }

    /**
     * Remove multiple items from storage
     * @param {string[]} keys - Array of storage keys
     * @returns {boolean} Success status
     */
    removeItems(keys) {
        try {
            if (!Array.isArray(keys)) {
                return false;
            }

            keys.forEach(key => this.removeItem(key));
            return true;
        } catch (error) {
            return false;
        }
    }

    /**
     * Check if a key exists in storage
     * @param {string} key - Storage key
     * @returns {boolean} Key existence status
     */
    hasItem(key) {
        try {
            const hashedKey = this._hashKey(key);
            return this.storage.getItem(hashedKey) !== null;
        } catch (error) {
            return false;
        }
    }

    /**
     * Clear all items from storage
     * @returns {boolean} Success status
     */
    clear() {
        try {
            this.storage.clear();
            return true;
        } catch (error) {
            return false;
        }
    }

    /**
     * Get all keys from storage
     * @returns {string[]} Array of storage keys
     */
    getAllKeys() {
        try {
            const keys = [];
            for (let i = 0; i < this.storage.length; i++) {
                const key = this.storage.key(i);
                if (key && !key.endsWith('_expires')) {
                    keys.push(key);
                }
            }
            return keys;
        } catch (error) {
            return [];
        }
    }

    /**
     * Get storage usage information
     * @returns {Object} Storage usage stats
     */
    getStorageInfo() {
        try {
            const keys = this.getAllKeys();
            let totalSize = 0;
            
            keys.forEach(key => {
                const value = this.storage.getItem(key);
                if (value) {
                    totalSize += value.length;
                }
            });

            return {
                type: this.storageType,
                totalKeys: keys.length,
                estimatedSizeBytes: totalSize,
                estimatedSizeKB: Math.round(totalSize / 1024 * 100) / 100,
                encryptionEnabled: this.encryptionEnabled,
                encryptionStatus: cryptoService.getStatus()
            };
        } catch (error) {
            return {
                type: this.storageType,
                totalKeys: 0,
                estimatedSizeBytes: 0,
                estimatedSizeKB: 0,
                encryptionEnabled: this.encryptionEnabled,
                encryptionStatus: cryptoService.getStatus()
            };
        }
    }

    /**
     * Serialize value for storage
     * @param {any} value - Value to serialize
     * @returns {string} Serialized (and optionally encrypted) value
     */
    _serialize(value) {
        try {
            // First serialize to JSON if needed
            let serialized;
            if (typeof value === 'string') {
                serialized = value;
            } else {
                serialized = JSON.stringify(value);
            }

            // Then encrypt if crypto is ready
            if (cryptoService.isEnabled()) {
                return cryptoService.encrypt(serialized);
            }

            return serialized;
        } catch (error) {
            // Fallback to string conversion
            return String(value);
        }
    }

    /**
     * Deserialize value from storage
     * @param {string} value - Value to deserialize
     * @returns {any} Deserialized (and optionally decrypted) value
     */
    _deserialize(value) {
        if (value === null || value === undefined) {
            return value;
        }

        try {
            // First decrypt if crypto is ready
            let decrypted = value;
            if (cryptoService.isEnabled()) {
                decrypted = cryptoService.decrypt(value);
            }

            // Check if the decrypted value looks like JSON (starts with { or [)
            // If not, it's likely a plain string (like JWT tokens)
            if (typeof decrypted === 'string' && 
                !decrypted.trim().startsWith('{') && 
                !decrypted.trim().startsWith('[')) {
                return decrypted;
            }

            // Try to parse as JSON only if it looks like JSON
            try {
                return JSON.parse(decrypted);
            } catch (parseError) {
                // If JSON parsing fails, return the raw string value silently
                return decrypted;
            }
        } catch (error) {
            // If all else fails, return the original value silently
            return value;
        }
    }

    /**
     * Switch storage backend
     * @param {string} newStorageType - New storage type
     * @returns {boolean} Success status
     */
    switchStorageType(newStorageType) {
        try {
            this.storageType = newStorageType;
            this.storage = this._getStorageBackend(newStorageType);
            return true;
        } catch (error) {
            return false;
        }
    }
}

// Create singleton instances for different storage types with encryption options
const localStorage = new StorageService(STORAGE_TYPES.LOCAL, { enableEncryption: true });
const sessionStorage = new StorageService(STORAGE_TYPES.SESSION, { enableEncryption: true });
const memoryStorage = new StorageService(STORAGE_TYPES.MEMORY, { enableEncryption: false });

// Export the service class and instances
export {
    StorageService,
    STORAGE_TYPES,
    localStorage as storageService,
    sessionStorage as sessionStorageService,
    memoryStorage as memoryStorageService
};

// Default export is the localStorage service
export default localStorage;
